import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  X, ChevronRight, CheckCircle, XCircle, Zap, Sparkles, ArrowRight, RotateCcw, MessageCircle, GraduationCap, Lightbulb, Volume2, VolumeX, Mic, Crown
} from "lucide-react";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import { UpgradeModal, DailyUsageBadge } from "../components/UpgradeModal";
import MathContent from "../components/MathContent";

const Lesson = () => {
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, fetchUser } = useAuth();
  
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sessionStats, setSessionStats] = useState({ correct: 0, total: 0, xp: 0 });
  const [lessonComplete, setLessonComplete] = useState(false);
  const [scaffoldingMode, setScaffoldingMode] = useState(searchParams.get('scaffolding') === 'true');
  const [showHint, setShowHint] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [interviewMode, setInterviewMode] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [spokenAnswer, setSpokenAnswer] = useState('');
  const [showSpokenFeedback, setShowSpokenFeedback] = useState(false);
  
  const [questionCount, setQuestionCount] = useState(5);

  // Freemium state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('');
  const [resetTime, setResetTime] = useState('');
  const [tierInfo, setTierInfo] = useState(null);
  const [eliminatedOptions, setEliminatedOptions] = useState([]);

  // Text-to-Speech for citizenship interview practice
  const speak = useCallback((text) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9; // Slightly slower for clarity
      utterance.pitch = 1;
      utterance.volume = 1;
      
      // Try to use a clear English voice
      const voices = window.speechSynthesis.getVoices();
      const englishVoice = voices.find(v => v.lang.startsWith('en-US')) || voices[0];
      if (englishVoice) utterance.voice = englishVoice;
      
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      
      window.speechSynthesis.speak(utterance);
    } else {
      toast.error("Text-to-speech not supported in this browser");
    }
  }, []);

  const stopSpeaking = useCallback(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  }, []);

  // Speech-to-Text for practicing verbal answers
  const startListening = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error("Speech recognition not supported in this browser. Try Chrome.");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      setSpokenAnswer('');
      setShowSpokenFeedback(false);
      toast.info("Listening... Speak your answer clearly", { duration: 2000 });
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSpokenAnswer(transcript);
      setShowSpokenFeedback(true);
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'no-speech') {
        toast.error("No speech detected. Please try again.");
      } else if (event.error === 'not-allowed') {
        toast.error("Microphone access denied. Please allow microphone access.");
      } else {
        toast.error("Error with speech recognition. Please try again.");
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  }, []);

  // Read question aloud for citizenship interview practice
  const readQuestionAloud = useCallback(() => {
    const currentQuestion = questions[currentIndex];
    if (currentQuestion) {
      speak(currentQuestion.question);
    }
  }, [questions, currentIndex, speak]);

  // Auto-read questions in interview mode for citizenship
  useEffect(() => {
    if (interviewMode && categoryId === 'citizenship' && questions[currentIndex] && !loading) {
      // Small delay before reading
      const timer = setTimeout(() => {
        readQuestionAloud();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, interviewMode, categoryId, questions, loading, readQuestionAloud]);

  // Fetch user tier info
  useEffect(() => {
    const fetchTierInfo = async () => {
      try {
        const tier = await api.getUserTier();
        setTierInfo(tier);
      } catch (err) {
        console.error("Failed to fetch tier info:", err);
      }
    };
    fetchTierInfo();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [categoryId, scaffoldingMode]);

  const fetchQuestions = async (useAI = true, count = null) => {
    const limit = count || questionCount;
    setLoading(true);
    try {
      if (useAI) {
        toast.info("Generating unique AI questions...", { duration: 2000 });
      }
      const data = await api.getQuestions(categoryId, limit, scaffoldingMode, !useAI);
      setQuestions(data);
      if (data.length > 0 && useAI) {
        toast.success(`${data.length} unique AI questions ready!`, { duration: 2000 });
      }
      // Refresh tier info after fetching questions
      const tier = await api.getUserTier();
      setTierInfo(tier);
    } catch (error) {
      // Check if it's a daily limit error
      if (error.response?.status === 403) {
        const detail = error.response.data?.detail;
        if (detail?.error === 'daily_limit_reached') {
          setUpgradeReason(detail.message || "You've reached today's free practice limit.");
          setResetTime(detail.reset_time || '');
          setShowUpgradeModal(true);
          setLoading(false);
          return;
        }
      }
      toast.error("Failed to load questions");
      navigate("/categories");
    } finally {
      setLoading(false);
    }
  };

  const generateAIQuestions = async () => {
    // Replace current questions with AI-generated ones
    fetchQuestions(true);
  };

  const generateNewQuestion = async () => {
    setGenerating(true);
    try {
      const newQuestion = await api.generateQuestion(categoryId, null, scaffoldingMode);
      setQuestions(prev => [...prev, newQuestion]);
      toast.success("New unique AI question added!", { duration: 2000 });
    } catch (error) {
      toast.error("Failed to generate question");
    } finally {
      setGenerating(false);
    }
  };

  const handleAnswerSelect = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  const handleCheckAnswer = async () => {
    if (selectedAnswer === null) return;

    const currentQuestion = questions[currentIndex];
    const correct = selectedAnswer === currentQuestion.correct_answer;
    setIsCorrect(correct);
    setShowResult(true);

    const xpEarned = correct ? 10 : 0;
    setSessionStats(prev => ({
      correct: prev.correct + (correct ? 1 : 0),
      total: prev.total + 1,
      xp: prev.xp + xpEarned
    }));

    if (correct) {
      toast.success(`+${xpEarned} XP! 🎉`, { duration: 2000 });
    }

    // Submit answer with topic tracking for mastery
    try {
      const result = await api.submitAnswer(
        currentQuestion.id,
        selectedAnswer,
        currentQuestion.correct_answer,
        categoryId,
        currentQuestion.topic,
        correct
      );
      
      // Show mastery progress if topic was tracked
      if (result.mastery_update) {
        const { topic, accuracy, mastered } = result.mastery_update;
        if (mastered) {
          toast.success(`🏆 You've mastered ${topic}!`, { duration: 3000 });
        } else if (accuracy >= 70) {
          toast.info(`${topic}: ${accuracy}% accuracy - almost mastered!`, { duration: 2000 });
        }
      }
      
      await api.updateProgress(categoryId, xpEarned, correct ? 1 : 0, 1);
      fetchUser();
    } catch (error) {
      console.error("Failed to update progress:", error);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setShowHint(false);
      setSpokenAnswer('');
      setShowSpokenFeedback(false);
      setEliminatedOptions([]);
    } else {
      setLessonComplete(true);
    }
  };

  const handleMoreQuestions = async (count = null) => {
    const limit = count || questionCount;
    setLessonComplete(false);
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setShowHint(false);
    setSpokenAnswer('');
    setShowSpokenFeedback(false);
    setEliminatedOptions([]);
    // Keep session stats — don't reset
    setLoading(true);
    try {
      toast.info(`Generating ${limit} more questions...`, { duration: 2000 });
      const data = await api.getQuestions(categoryId, limit, scaffoldingMode, false);
      setQuestions(data);
      toast.success(`${data.length} new questions loaded!`, { duration: 2000 });
    } catch (error) {
      if (error.response?.status === 403) {
        const detail = error.response.data?.detail;
        if (detail?.error === 'daily_limit_reached') {
          setUpgradeReason(detail.message || "You've reached today's free practice limit.");
          setResetTime(detail.reset_time || '');
          setShowUpgradeModal(true);
        }
      } else {
        toast.error("Failed to load more questions");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setShowHint(false);
    setSpokenAnswer('');
    setShowSpokenFeedback(false);
    setEliminatedOptions([]);
    setSessionStats({ correct: 0, total: 0, xp: 0 });
    setLessonComplete(false);
    fetchQuestions(); // This will generate new unique questions
  };

  const toggleScaffolding = () => {
    setScaffoldingMode(!scaffoldingMode);
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setShowHint(false);
    setSpokenAnswer('');
    setShowSpokenFeedback(false);
    setEliminatedOptions([]);
    setSessionStats({ correct: 0, total: 0, xp: 0 });
    setLessonComplete(false);
  };

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;

  const categoryColors = {
    sat: { bg: "bg-indigo-500", light: "bg-indigo-50", text: "text-indigo-600" },
    act: { bg: "bg-lime-500", light: "bg-lime-50", text: "text-lime-600" },
    asvab: { bg: "bg-orange-500", light: "bg-orange-50", text: "text-orange-600" },
    staar: { bg: "bg-sky-500", light: "bg-sky-50", text: "text-sky-600" },
    citizenship: { bg: "bg-red-500", light: "bg-red-50", text: "text-red-600" }
  };

  const colors = categoryColors[categoryId] || categoryColors.sat;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="animate-spin">
          <Sparkles className={`w-16 h-16 ${colors.text}`} />
        </div>
        <p className="text-slate-600 font-medium">Generating unique questions...</p>
        <p className="text-slate-400 text-sm">AI is creating fresh questions just for you</p>
      </div>
    );
  }

  if (lessonComplete) {
    const accuracy = sessionStats.total > 0 ? Math.round((sessionStats.correct / sessionStats.total) * 100) : 0;
    
    return (
      <div className="max-w-lg mx-auto text-center animate-pop-in" data-testid="lesson-complete">
        <div className="card-pop p-8">
          <div className="w-24 h-24 bg-lime-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-5xl">🎉</span>
          </div>
          <h1 className="font-heading font-black text-3xl text-slate-800 mb-2">Lesson Complete!</h1>
          <p className="text-slate-500 mb-6">Great job finishing this practice session</p>

          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className={`${colors.light} rounded-2xl p-4`}>
              <p className={`font-heading font-black text-2xl ${colors.text}`}>{sessionStats.xp}</p>
              <p className="text-sm text-slate-500">XP Earned</p>
            </div>
            <div className="bg-lime-50 rounded-2xl p-4">
              <p className="font-heading font-black text-2xl text-lime-600">{sessionStats.correct}</p>
              <p className="text-sm text-slate-500">Correct</p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-4">
              <p className="font-heading font-black text-2xl text-slate-600">{accuracy}%</p>
              <p className="text-sm text-slate-500">Accuracy</p>
            </div>
          </div>

          <div className="space-y-3">
            {/* Keep Going — more questions without resetting stats */}
            <div className="flex gap-2">
              <button
                onClick={() => handleMoreQuestions(5)}
                data-testid="more-5-btn"
                className="flex-1 btn-primary flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                +5 More
              </button>
              <button
                onClick={() => handleMoreQuestions(10)}
                data-testid="more-10-btn"
                className="flex-1 btn-primary flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                +10 More
              </button>
              <button
                onClick={() => handleMoreQuestions(20)}
                data-testid="more-20-btn"
                className="flex-1 btn-primary flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                +20 More
              </button>
            </div>
            <button
              onClick={handleRestart}
              data-testid="practice-again-btn"
              className="w-full btn-secondary flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-5 h-5" />
              Start Fresh (Reset Stats)
            </button>
            <button
              onClick={() => navigate("/tutor")}
              data-testid="ask-tutor-btn"
              className="w-full btn-secondary flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Ask AI Tutor
            </button>
            <button
              onClick={() => navigate("/categories")}
              className="w-full text-slate-500 font-bold py-3 hover:text-slate-700"
            >
              Back to Categories
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="lesson-page">
      {/* Citizenship Interview Practice Mode */}
      {categoryId === 'citizenship' && (
        <div className="mb-4 p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-red-600" />
              <span className="font-medium text-red-800">Interview Practice Mode</span>
              <span className="text-xs text-red-600">(Questions read aloud)</span>
            </div>
            <Switch 
              checked={interviewMode} 
              onCheckedChange={setInterviewMode}
              data-testid="interview-mode-toggle"
            />
          </div>
          {interviewMode && (
            <p className="text-xs text-red-600 mt-2">
              Questions will be read aloud to simulate the citizenship interview. Listen carefully and select your answer.
            </p>
          )}
        </div>
      )}

      {/* Emergent Bilingual (EB) Scaffolding Toggle */}
      <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-purple-800">Emergent Bilingual (EB) Mode</span>
            <span className="text-xs text-purple-600">(Simplified language)</span>
          </div>
          <Switch 
            checked={scaffoldingMode} 
            onCheckedChange={toggleScaffolding}
            data-testid="scaffolding-toggle"
          />
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate("/categories")}
          className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-slate-200 transition-colors"
          data-testid="close-lesson-btn"
        >
          <X className="w-5 h-5 text-slate-500" />
        </button>
        
        <div className="flex-1 mx-4">
          <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className={`h-full ${colors.bg} rounded-full transition-all duration-500`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="flex items-center gap-1 text-slate-500 font-bold">
          <span>{currentIndex + 1}</span>
          <span>/</span>
          <span>{questions.length}</span>
        </div>
      </div>

      {/* Question Card */}
      <div className="card-pop mb-6 animate-slide-up" data-testid="question-card">
        {/* Topic Badge and Controls */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {currentQuestion?.topic && (
              <div className={`inline-block ${colors.light} ${colors.text} px-3 py-1 rounded-full text-sm font-bold`}>
                {currentQuestion.topic}
              </div>
            )}
            {/* Read Aloud Button for Citizenship */}
            {categoryId === 'citizenship' && (
              <button
                onClick={isSpeaking ? stopSpeaking : readQuestionAloud}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  isSpeaking 
                    ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                    : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                }`}
                data-testid="read-aloud-btn"
              >
                {isSpeaking ? (
                  <>
                    <VolumeX className="w-4 h-4" />
                    Stop
                  </>
                ) : (
                  <>
                    <Volume2 className="w-4 h-4" />
                    Read Aloud
                  </>
                )}
              </button>
            )}
          </div>
          {currentQuestion?.hint && !showResult && (
            <button
              onClick={() => setShowHint(!showHint)}
              className="flex items-center gap-1 text-amber-600 hover:text-amber-700 text-sm font-medium"
              data-testid="hint-btn"
            >
              <Lightbulb className="w-4 h-4" />
              {showHint ? "Hide Hint" : "Need a Hint?"}
            </button>
          )}
        </div>

        {/* Hint Display */}
        {showHint && currentQuestion?.hint && !showResult && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800 text-sm">{currentQuestion.hint}</p>
            </div>
          </div>
        )}

        {/* Say Your Answer - Microphone Feature for Citizenship */}
        {categoryId === 'citizenship' && !showResult && (
          <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Mic className={`w-5 h-5 ${isListening ? 'text-red-500 animate-pulse' : 'text-green-600'}`} />
                <span className="font-medium text-green-800">Practice Speaking Your Answer</span>
              </div>
              <button
                onClick={startListening}
                disabled={isListening}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all ${
                  isListening 
                    ? 'bg-red-500 text-white animate-pulse' 
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
                data-testid="speak-answer-btn"
              >
                <Mic className="w-4 h-4" />
                {isListening ? 'Listening...' : 'Say Your Answer'}
              </button>
            </div>
            <p className="text-xs text-green-600">
              Click the button and speak your answer clearly, just like in the real citizenship interview.
            </p>
            
            {/* Spoken Answer Feedback */}
            {showSpokenFeedback && spokenAnswer && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-green-300">
                <p className="text-sm text-slate-600 mb-1">You said:</p>
                <p className="font-medium text-slate-800 italic">"{spokenAnswer}"</p>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => speak(spokenAnswer)}
                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Volume2 className="w-3 h-3" /> Hear what you said
                  </button>
                  <button
                    onClick={startListening}
                    className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
                  >
                    <Mic className="w-3 h-3" /> Try again
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Question */}
        <h2 className="font-heading font-bold text-xl text-slate-800 mb-6">
          <MathContent text={currentQuestion?.question} />
        </h2>

        {/* Options */}
        <div className="space-y-3">
          {currentQuestion?.options.map((option, index) => {
            const isEliminated = eliminatedOptions.includes(index);
            let optionClass = "border-2 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50";
            
            if (showResult) {
              if (index === currentQuestion.correct_answer) {
                optionClass = "border-2 border-lime-400 bg-lime-50";
              } else if (index === selectedAnswer && !isCorrect) {
                optionClass = "border-2 border-red-400 bg-red-50 animate-shake";
              } else {
                optionClass = "border-2 border-slate-200 opacity-50";
              }
            } else if (isEliminated) {
              optionClass = "border-2 border-orange-300 bg-orange-50 opacity-60 line-through";
            } else if (selectedAnswer === index) {
              optionClass = "border-2 border-indigo-400 bg-indigo-50";
            }

            return (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                disabled={showResult}
                data-testid={`option-${index}`}
                className={`w-full p-4 rounded-2xl text-left font-medium transition-all ${optionClass}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    showResult && index === currentQuestion.correct_answer
                      ? "bg-lime-500 text-white"
                      : showResult && index === selectedAnswer && !isCorrect
                      ? "bg-red-500 text-white"
                      : isEliminated
                      ? "bg-orange-400 text-white"
                      : selectedAnswer === index
                      ? "bg-indigo-500 text-white"
                      : "bg-slate-100 text-slate-600"
                  }`}>
                    {showResult && index === currentQuestion.correct_answer ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : showResult && index === selectedAnswer && !isCorrect ? (
                      <XCircle className="w-5 h-5" />
                    ) : isEliminated ? (
                      <X className="w-5 h-5" />
                    ) : (
                      String.fromCharCode(65 + index)
                    )}
                  </div>
                  <span className={`text-slate-700 ${isEliminated ? "line-through text-slate-400" : ""}`}><MathContent text={option} /></span>
                  {isEliminated && <span className="text-xs text-orange-600 ml-auto">Eliminated</span>}
                </div>
              </button>
            );
          })}
        </div>

        {/* Explanation */}
        {showResult && (
          <div className={`mt-6 p-4 rounded-2xl ${isCorrect ? "bg-lime-50 border-2 border-lime-200" : "bg-orange-50 border-2 border-orange-200"} animate-slide-up`}>
            <div className="flex items-start gap-3">
              {isCorrect ? (
                <CheckCircle className="w-6 h-6 text-lime-500 flex-shrink-0" />
              ) : (
                <XCircle className="w-6 h-6 text-orange-500 flex-shrink-0" />
              )}
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className={`font-bold ${isCorrect ? "text-lime-700" : "text-orange-700"}`}>
                    {isCorrect ? "Correct! 🎉" : "Not quite right"}
                  </p>
                  {/* Read Explanation Button for Citizenship */}
                  {categoryId === 'citizenship' && currentQuestion?.explanation && (
                    <button
                      onClick={() => speak(`The correct answer is: ${currentQuestion.options[currentQuestion.correct_answer]}. ${currentQuestion.explanation}`)}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                      data-testid="read-explanation-btn"
                    >
                      <Volume2 className="w-4 h-4" />
                      Hear Answer
                    </button>
                  )}
                </div>
                <p className="text-slate-600 mt-1"><MathContent text={currentQuestion?.explanation} /></p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        {!showResult ? (
          <button
            onClick={handleCheckAnswer}
            disabled={selectedAnswer === null}
            data-testid="check-answer-btn"
            className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            Check Answer
            <ChevronRight className="w-5 h-5" />
          </button>
        ) : (
          <button
            onClick={handleNext}
            data-testid="next-btn"
            className="flex-1 btn-primary flex items-center justify-center gap-2"
          >
            {currentIndex < questions.length - 1 ? "Next Question" : "Finish Lesson"}
            <ArrowRight className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Generate AI Question */}
      <div className="mt-4 flex gap-2">
        <button
          onClick={generateNewQuestion}
          disabled={generating}
          data-testid="generate-question-btn"
          className="flex-1 btn-secondary flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Sparkles className={`w-5 h-5 ${generating ? "animate-spin" : ""}`} />
          {generating ? "Generating..." : "+ Add AI Question"}
        </button>
        <button
          onClick={generateAIQuestions}
          disabled={loading}
          data-testid="generate-all-ai-btn"
          className="px-4 btn-secondary flex items-center justify-center gap-2 disabled:opacity-50 text-indigo-600"
          title="Replace all questions with unique AI-generated ones"
        >
          <Sparkles className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          AI Set
        </button>
      </div>

      {/* Session Stats */}
      <div className="mt-6 flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-1 text-lime-600">
          <CheckCircle className="w-4 h-4" />
          <span className="font-bold">{sessionStats.correct} correct</span>
        </div>
        <div className="flex items-center gap-1 text-indigo-600">
          <Zap className="w-4 h-4" />
          <span className="font-bold">{sessionStats.xp} XP</span>
        </div>
      </div>

      {/* Daily Usage Badge for Free Users */}
      {tierInfo && !tierInfo.is_premium && (
        <div className="mt-4 flex justify-center">
          <DailyUsageBadge 
            used={tierInfo.daily_usage?.questions_used || 0} 
            limit={tierInfo.daily_usage?.questions_limit || 5} 
            type="questions"
          />
        </div>
      )}

      {/* Premium Badge */}
      {tierInfo?.is_premium && (
        <div className="mt-4 flex justify-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-white text-sm font-bold rounded-full">
            <Crown className="w-4 h-4" />
            Unlimited Practice
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => {
          setShowUpgradeModal(false);
          navigate("/categories");
        }}
        reason={upgradeReason}
        resetTime={resetTime}
      />
    </div>
  );
};

export default Lesson;
